import { useTranslation } from '@checkout/core'
import { useState, useCallback } from 'react'

/** 邮箱验证结果 */
export interface EmailValidationState {
  /** 是否已触及（blur 过） */
  touched: boolean
  /** 错误信息 */
  error: string | null
}

export interface UseEmailValidationOptions {
  /** 初始邮箱值 */
  initialEmail?: string
}

export interface UseEmailValidationResult {
  /** 验证状态 */
  state: EmailValidationState
  /** 是否有效 */
  isValid: boolean
  /** 处理 blur */
  handleBlur: (email: string) => void
  /** 处理 change（仅在 touched 后验证） */
  handleChange: (email: string) => void
  /** 手动验证 */
  validate: (email: string) => boolean
  /** 重置状态 */
  reset: () => void
  /** 设置外部错误（如 API 返回的错误） */
  setError: (error: string | null) => void
}

/** 邮箱验证正则 */
const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/

/** 验证邮箱格式 */
export const isValidEmail = (email: string): boolean => {
  const trimmed = email.trim()
  if (!trimmed || !trimmed.includes('@') || !trimmed.includes('.')) {
    return false
  }
  return EMAIL_REGEX.test(trimmed)
}

export function useEmailValidation(
  _options: UseEmailValidationOptions = {}
): UseEmailValidationResult {
  const { t } = useTranslation()

  const [state, setState] = useState<EmailValidationState>({
    touched: false,
    error: null,
  })

  /** 验证并返回错误信息 */
  const validateEmail = useCallback(
    (email: string): string | null => {
      const trimmed = email.trim()
      if (!trimmed) {
        return t('validation.emailRequired') || 'Email is required'
      }
      if (!isValidEmail(trimmed)) {
        return t('validation.emailInvalid') || 'Please enter a valid email address'
      }
      return null
    },
    [t]
  )

  /** 处理 blur - 标记为 touched 并验证 */
  const handleBlur = useCallback(
    (email: string) => {
      const error = validateEmail(email)
      setState({ touched: true, error })
    },
    [validateEmail]
  )

  /** 处理 change - 仅在 touched 后验证 */
  const handleChange = useCallback(
    (email: string) => {
      setState((prev) => {
        if (!prev.touched) return prev
        const error = validateEmail(email)
        return { ...prev, error }
      })
    },
    [validateEmail]
  )

  /** 手动验证 */
  const validate = useCallback(
    (email: string): boolean => {
      const error = validateEmail(email)
      setState({ touched: true, error })
      return error === null
    },
    [validateEmail]
  )

  /** 重置状态 */
  const reset = useCallback(() => {
    setState({ touched: false, error: null })
  }, [])

  /** 设置外部错误（如 API 返回的错误） */
  const setError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, touched: true, error }))
  }, [])

  return {
    state,
    isValid: state.touched && state.error === null,
    handleBlur,
    handleChange,
    validate,
    reset,
    setError,
  }
}

export default useEmailValidation
